import React from 'react';

import { classnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { ExpansionPanel, ExpansionPanelProps } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { useExpandableTreeContext } from '../../hooks/useExpandableTreeContext';

import './index.scss';

export const CLASSNAME = 'expandable-tree-panel';

export interface ExpandableTreePanelProps extends Omit<ExpansionPanelProps, 'toggleButtonProps'> {
    /** scope for tracking purposes */
    scope: string;
    /** optional classname */
    className?: string;
    /** current rendered level */
    level?: number;
    /** node children */
    children?: React.ReactNode;
    /** whether the item has children or not */
    hasChildren?: boolean;
    /** whether the panel is hoverable or not */
    isHoverable?: boolean;
    /** is the tree opened */
    isOpen?: boolean;
    /** control whether the exapandable tree is open or not */
    setIsOpen?: (open: boolean) => void;
    /** is the tree opened */
    canBeToggle?: boolean;
    /** expansion panel toggle custom styles */
    expansionToggleStyles?: Partial<HTMLButtonElement['style']>;
    /** header to be displayed for the expansion panel */
    header: React.ReactNode;
    /** references to the expansion panel */
    expansionPanelRef?: ExpansionPanelProps['ref'];
    /** callback on click on the expansion panel, used in combination with isHoverable */
    onClick?: HTMLButtonElement['click'];
    /** item identifier */
    itemId?: string;
    /**
     * Whether there should only be one panel open at a time.
     */
    shouldOnlyHaveOneOpenPanel?: boolean;
}

export const ExpandableTreePanel: React.FC<ExpandableTreePanelProps> = ({
    scope,
    className,
    level = 0,
    children,
    hasChildren = false,
    isOpen: isDefaultOpen = false,
    canBeToggle = true,
    setIsOpen,
    expansionToggleStyles,
    expansionPanelRef,
    isHoverable,
    header,
    onClick,
    itemId,
    shouldOnlyHaveOneOpenPanel = false,
    ...props
}) => {
    const [isOpen, setOpen] = React.useState(isDefaultOpen);
    const { translateKey } = useTranslate();
    const { get } = useDataAttributes(scope);
    const hiddenToggleStyles = !canBeToggle ? { display: 'none' } : { visibility: 'hidden' };
    const toggleStyles = hasChildren && canBeToggle ? {} : hiddenToggleStyles;
    const { isOpenWhenContextIsEnabled, onOpenMenu } = useExpandableTreeContext({ itemId });

    const handleOnToggle = (open: boolean) => {
        if (canBeToggle && !setIsOpen) {
            setOpen(open);
        } else if (canBeToggle && setIsOpen) {
            setIsOpen(open);
        }

        onOpenMenu(open);
    };

    const isExpansionPanelOpen = setIsOpen ? isDefaultOpen : isOpen;

    const expansionPanel = (
        <ExpansionPanel
            {...props}
            ref={expansionPanelRef}
            className={classnames(CLASSNAME, `${CLASSNAME}__level-${level % 2 === 0 ? 'even' : 'odd'}`, className)}
            isOpen={shouldOnlyHaveOneOpenPanel ? isOpenWhenContextIsEnabled : isExpansionPanelOpen}
            onToggleOpen={handleOnToggle}
            toggleButtonProps={{
                label: translateKey(GLOBAL.TOGGLE),
                style: expansionToggleStyles || toggleStyles,
                ...get({
                    element: 'item',
                    action: 'expand',
                }),
            }}
        >
            <header className={`${CLASSNAME}__content`}>{header}</header>

            {children}
        </ExpansionPanel>
    );

    /**
     * To make this component reusable and hoverable, we are wrapping the resulting component in a button
     * so that we can use the correct HTML tags for a clickable and hoverable item.
     */
    if (isHoverable && onClick) {
        return (
            // this is specifically using a native button instead of the DS one since no customizations are needed.
            // eslint-disable-next-line react/forbid-elements
            <button
                aria-label={translateKey(GLOBAL.OPEN)}
                className={classnames(CLASSNAME, className, `${CLASSNAME}--hoverable`)}
                type="button"
                onClick={() => (onClick ? onClick() : null)}
            >
                {expansionPanel}
            </button>
        );
    }

    return expansionPanel;
};
